import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TenantOnboardingService } from '../../../core/services/tenant-onboarding.service';
import { trackFunnelEventOnce } from '../../analytics/google-analytics-tracking';
import { AuthService } from '../../../../services/auth.service';
import { API_ERROR_CODES } from '../../../shared/constants/api-error-codes';
import { COMPANY_SIZE_OPTIONS } from '../../../shared/constants/company-size.constants';
import { SITE_FOOTER_COPYRIGHT } from '../../../shared/constants/app.constants';
import { PasswordVisibilityToggleComponent } from '../../../shared/ui/password-visibility-toggle.component';
import { CountrySelectComponent } from '../../../shared/ui/country-select.component';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    PasswordVisibilityToggleComponent,
    CountrySelectComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.css',
})
export class SignupComponent implements OnInit {
  /** True when `/signup?token=…` (admin-provisioned custom-apps-shopify); work email is fixed to the login identity. */
  get isCustomAppsOnboardingSignup(): boolean {
    return Boolean(this.customAppsOnboardingToken?.trim());
  }

  readonly siteFooterCopyright = SITE_FOOTER_COPYRIGHT;
  readonly companySizeOptions = COMPANY_SIZE_OPTIONS;
  readonly loading = signal(false);
  /** Custom-apps `?token=` flow: checking token with API before enabling the form. */
  readonly verifyingOnboardingToken = signal(false);
  readonly onboardingTokenFatalError = signal<string | null>(null);
  readonly formError = signal<string | null>(null);
  readonly fieldErrors = signal<Record<string, string>>({});
  readonly requirePassword = signal(false);
  /** When signup requires a password (email verification flow), allow toggling visibility. */
  readonly passwordVisible = signal(false);

  private readonly signupSessionToken: string | null;
  /** From /signup?token= or sessionStorage after login (admin-provisioned custom-apps-shopify). */
  private customAppsOnboardingToken: string | null;
  /** Work email fixed to the authenticated (or Google-preflight) identity for custom-apps token signup. */
  private customAppsLockedWorkEmail: string | null = null;
  /** True when redirecting unauthenticated user to /login before custom-apps signup. */
  private redirectedForCustomAppsLogin = false;

  readonly signupForm = this.formBuilder.group({
    email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
    name: ['', [Validators.required]],
    company_name: ['', [Validators.required]],
    brand_name: ['', [Validators.required]],
    company_size: ['', [Validators.required]],
    country: ['', [Validators.required]],
    mobile: ['', [Validators.required, Validators.pattern(/^\+?[0-9\s()-]{7,20}$/)]],
    password: ['', []],
  });

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly onboardingService: TenantOnboardingService,
  ) {
    const state = router.getCurrentNavigation()?.extras.state ?? history.state;
    const email = typeof state?.['email'] === 'string' ? state['email'] : '';
    const name = typeof state?.['name'] === 'string' ? state['name'] : '';
    const signupSession =
      typeof state?.['signupSession'] === 'string' ? state['signupSession'] : null;

    const googleSignup = this.authService.peekGoogleSignupSession();
    const googleSignupEmail = googleSignup?.email ?? '';
    const googleSignupName = googleSignup?.name ?? '';

    this.signupSessionToken = signupSession;
    this.requirePassword.set(Boolean(signupSession));

    const queryToken = this.route.snapshot.queryParamMap.get('token')?.trim() ?? null;
    if (queryToken) {
      this.authService.persistCustomAppsOnboardingTokenFromQuery(queryToken);
    }

    const isEmailVerificationSignup = Boolean(email) || Boolean(signupSession);
    const isGoogleSignup = Boolean(googleSignup?.idToken);
    let resolvedOnboardingToken: string | null = queryToken;
    if (!resolvedOnboardingToken && !isEmailVerificationSignup && !isGoogleSignup) {
      resolvedOnboardingToken = this.authService.readCustomAppsOnboardingTokenFromSession();
    }

    const hasCustomAppsIntent = Boolean(resolvedOnboardingToken);
    const needsLoginForCustomApps =
      hasCustomAppsIntent &&
      !this.authService.isAuthenticated() &&
      !isEmailVerificationSignup;

    // Email-verification flow passes state from /auth/verify; custom-app uses ?token= or restored sessionStorage.
    if (!email && !signupSession && !hasCustomAppsIntent && !isGoogleSignup) {
      this.customAppsOnboardingToken = null;
      void this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    if (needsLoginForCustomApps) {
      this.authService.storePendingSignupReturnAfterLogin();
      this.redirectedForCustomAppsLogin = true;
      this.customAppsOnboardingToken = null;
      const inviteToken = String(resolvedOnboardingToken ?? '').trim();
      void this.router.navigate(['/login'], {
        replaceUrl: true,
        queryParams: {
          returnUrl: '/signup',
          // Keep invite in the URL through login (bookmark/refresh); login also persists to sessionStorage.
          token: inviteToken,
        },
        state: {
          message: 'Please sign in to continue store registration.',
        },
      });
    } else {
      this.customAppsOnboardingToken = resolvedOnboardingToken;
    }

    if (
      this.customAppsOnboardingToken &&
      !this.redirectedForCustomAppsLogin &&
      this.authService.hasValidSession() &&
      this.authService.getUserTypeFromToken() === 'platform_user'
    ) {
      this.authService.clearCustomAppsMerchantInviteFlow();
      this.customAppsOnboardingToken = null;
      void this.router.navigate(['/platform'], {
        replaceUrl: true,
        state: {
          message:
            'Merchant store signup is for store owner accounts. Sign out and open the invite link with a Google account that is not a platform administrator, or ask your admin for help.',
        },
      });
      return;
    }

    if (this.customAppsOnboardingToken && !this.redirectedForCustomAppsLogin) {
      if (this.authService.hasValidSession()) {
        const j = this.authService.getCurrentUserDisplayInfo().email?.trim();
        this.customAppsLockedWorkEmail = j ? j : null;
      } else if (email.trim()) {
        this.customAppsLockedWorkEmail = email.trim();
      }
    }

    if (this.customAppsOnboardingToken) {
      this.verifyingOnboardingToken.set(true);
      this.signupForm.disable({ emitEvent: false });
    }

    const workEmail =
      this.customAppsLockedWorkEmail?.trim() ||
      email ||
      googleSignupEmail ||
      (this.authService.hasValidSession()
        ? this.authService.getCurrentUserDisplayInfo().email?.trim() ?? ''
        : '');
    this.signupForm.patchValue({
      email: workEmail,
      name: name || googleSignupName,
    });

    if (this.requirePassword()) {
      this.signupForm
        .get('password')
        ?.setValidators([Validators.required, Validators.minLength(8)]);
      this.signupForm.get('password')?.updateValueAndValidity();
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.redirectedForCustomAppsLogin) {
      return;
    }
    const token = this.customAppsOnboardingToken?.trim();
    if (!token) {
      return;
    }
    try {
      await this.authService.verifyCustomAppsOnboardingToken(token);
      this.signupForm.enable({ emitEvent: false });
      this.applyCustomAppsLockedWorkEmailControl();
    } catch (err: unknown) {
      this.onboardingTokenFatalError.set(this.authService.resolveAuthError(err));
      this.authService.clearCustomAppsOnboardingTokenFromSession();
    } finally {
      this.verifyingOnboardingToken.set(false);
    }
  }

  togglePasswordVisibility(): void {
    this.passwordVisible.update((v) => !v);
  }

  /** After `enable()`, re-lock work email for custom-apps invite signup. */
  private applyCustomAppsLockedWorkEmailControl(): void {
    if (!this.customAppsOnboardingToken?.trim()) {
      return;
    }
    let locked =
      this.customAppsLockedWorkEmail?.trim() ||
      (this.authService.hasValidSession()
        ? this.authService.getCurrentUserDisplayInfo().email?.trim() ?? ''
        : '');
    if (!locked) {
      return;
    }
    this.customAppsLockedWorkEmail = locked;
    const c = this.signupForm.get('email');
    if (!c) {
      return;
    }
    c.patchValue(locked, { emitEvent: false });
    c.disable({ emitEvent: false });
  }

  fieldError(fieldName: string): string | null {
    const apiFieldError = this.fieldErrors()[fieldName];
    if (apiFieldError) {
      return apiFieldError;
    }

    const control = this.signupForm.get(fieldName);
    if (!control || !control.invalid || !control.touched) {
      return null;
    }

    if (control.hasError('required')) {
      const requiredMessages: Record<string, string> = {
        name: 'Please enter your full name.',
        company_name: 'Please enter your company name.',
        brand_name: 'Please enter your brand name.',
        company_size: 'Please select your company size.',
        country: 'Please select your country.',
        mobile: 'Please enter your mobile number.',
        email: 'Please enter your email address.',
        password: 'Please enter a password.',
      };
      return requiredMessages[fieldName] ?? 'Please complete this field.';
    }

    if (control.hasError('pattern') && fieldName === 'mobile') {
      return 'Please enter a valid mobile number.';
    }

    if (control.hasError('minlength') && fieldName === 'password') {
      return 'Password must be at least 8 characters.';
    }

    return null;
  }

  async submit(): Promise<void> {
    if (
      this.customAppsOnboardingToken &&
      (this.onboardingTokenFatalError() || this.verifyingOnboardingToken())
    ) {
      return;
    }

    this.formError.set(null);
    this.fieldErrors.set({});

    this.signupForm.markAllAsTouched();
    if (this.signupForm.invalid) {
      return;
    }

    const raw = this.signupForm.getRawValue();
    this.loading.set(true);

    try {
      const payload = {
        email: raw.email ?? '',
        name: (raw.name ?? '').trim(),
        company_name: (raw.company_name ?? '').trim(),
        brand_name: (raw.brand_name ?? '').trim(),
        company_size: this.normalizeOptional(raw.company_size),
        country: this.normalizeOptional(raw.country),
        mobile: this.normalizeOptional(raw.mobile),
        ...(this.signupSessionToken
          ? {
              signupSession: this.signupSessionToken,
              password: raw.password ?? '',
            }
          : {}),
        ...(this.customAppsOnboardingToken
          ? {
              custom_apps_shopify_onboarding_token: this.customAppsOnboardingToken,
            }
          : {}),
      };

      const signupResponse = await this.authService.completeTenantSignup(
        payload,
      );
      const slug =
        (typeof signupResponse.slug === 'string' && signupResponse.slug.trim()) ||
        (typeof signupResponse.tenant?.slug === 'string' && signupResponse.tenant.slug.trim()) ||
        null;

      if (!slug) {
        await this.router.navigate(['/login'], { replaceUrl: true });
        return;
      }

      trackFunnelEventOnce(`sign_up:${slug}`, 'sign_up', {
        method: this.signupSessionToken ? 'email_verification' : 'tenant_form',
      });

      this.onboardingService.clearCache();
      if (this.customAppsOnboardingToken) {
        this.authService.clearCustomAppsOnboardingTokenFromSession();
      }
      const forward = this.pickPostSignupQueryParams();
      await this.router.navigate(['/t', slug], {
        replaceUrl: true,
        queryParams: forward && Object.keys(forward).length ? forward : undefined,
      });
    } catch (error) {
      this.handleSignupError(error);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Preserve e.g. `?shop=…&mode=custom-apps-shopify` for onboarding (non-token flows).
   * Token-based custom-app signup does not forward shop/mode (token consumed in API).
   */
  private pickPostSignupQueryParams(): Record<string, string> | undefined {
    if (this.customAppsOnboardingToken) {
      return { mode: 'custom-apps-shopify' };
    }
    const shop = this.route.snapshot.queryParamMap.get('shop')?.trim();
    const mode = this.route.snapshot.queryParamMap.get('mode')?.trim();
    if (!shop && !mode) return undefined;
    const out: Record<string, string> = {};
    if (shop) out['shop'] = shop;
    if (mode) out['mode'] = mode;
    return out;
  }

  private normalizeOptional(value: string | null | undefined): string | undefined {
    const normalized = (value ?? '').trim();
    return normalized.length > 0 ? normalized : undefined;
  }

  private handleSignupError(error: unknown): void {
    if (!(error instanceof HttpErrorResponse)) {
      this.formError.set(this.authService.resolveAuthError(error));
      return;
    }

    if (
      error.status === 429 ||
      error.status === 503 ||
      (error.status === 403 &&
        typeof error.error === 'object' &&
        error.error !== null &&
        ((error.error as { code?: unknown }).code ===
          API_ERROR_CODES.SELF_SERVE_SIGNUP_DISABLED ||
          (error.error as { code?: unknown }).code ===
            API_ERROR_CODES.TURNSTILE_VERIFICATION_FAILED))
    ) {
      this.formError.set(this.authService.resolveAuthError(error));
      return;
    }

    const payload = error.error;
    const nextFieldErrors: Record<string, string> = {};

    if (payload && typeof payload === 'object' && 'errors' in payload) {
      const errors = payload.errors as Record<string, unknown>;
      for (const [key, value] of Object.entries(errors)) {
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          nextFieldErrors[key] = this.humanizeSignupApiFieldError(key, value[0]);
          continue;
        }
        if (typeof value === 'string') {
          nextFieldErrors[key] = this.humanizeSignupApiFieldError(key, value);
        }
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      this.fieldErrors.set(nextFieldErrors);
      return;
    }

    if (Array.isArray(payload?.message)) {
      const lines = (payload.message as unknown[]).filter((x): x is string => typeof x === 'string');
      this.formError.set(this.humanizeSignupAggregatedMessages(lines));
      return;
    }

    if (typeof payload?.message === 'string') {
      this.formError.set(this.humanizeSignupAggregatedMessages([payload.message]));
      return;
    }

    this.formError.set(this.authService.resolveAuthError(error));
  }

  /**
   * Turn class-validator / Nest messages into short, user-facing copy.
   */
  private humanizeSignupApiFieldError(fieldKey: string, rawMessage: string): string {
    const low = (rawMessage ?? '').toLowerCase().trim();

    switch (fieldKey) {
      case 'company_size':
        return 'Please select your company size.';
      case 'country':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter your country.';
        }
        return 'Please enter a valid country.';
      case 'mobile':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter your mobile number.';
        }
        return 'Please enter a valid mobile number.';
      case 'company_name':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter your company name.';
        }
        return 'Please check your company name and try again.';
      case 'brand_name':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter your brand name.';
        }
        return 'Please check your brand name and try again.';
      case 'name':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter your full name.';
        }
        return 'Please check your name and try again.';
      case 'email':
        return 'Please enter a valid email address.';
      case 'password':
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please enter a password.';
        }
        return 'Please check your password and try again.';
      case 'signupSession':
        return 'Your signup session expired. Please return to login and request a new link.';
      default:
        if (low.includes('should not be empty') || low.includes('is required')) {
          return 'Please complete this field.';
        }
        if (low.includes('must be a string')) {
          return 'Please check this field and try again.';
        }
        return rawMessage.trim() || 'Something went wrong. Please try again.';
    }
  }

  /** One readable sentence from comma-separated or multi-line API validation text. */
  private humanizeSignupAggregatedMessages(lines: string[]): string {
    const parts: string[] = [];
    for (const line of lines) {
      for (const segment of line.split(',').map((s) => s.trim()).filter(Boolean)) {
        const m = /^([a-z][a-z0-9_]*)\s+(.+)$/i.exec(segment);
        if (m) {
          parts.push(this.humanizeSignupApiFieldError(m[1], m[2]));
        } else {
          parts.push(segment);
        }
      }
    }
    return [...new Set(parts)].join(' ');
  }
}
